import type { TranslatedString } from '@interfaces/shared';
import {
  TRADING_PROCESS_LIST,
  TradingProcessValue,
} from '@interfaces/tradingProcessList';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import ModelPortfoliosSelect from '@ovComponents/3-pattern/legacyModelPortfoliosSelect/modelPortfoliosSelect';
import ThemeSelect from '@ovComponents/3-pattern/legacyThemeSelect/themeSelect';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { UserContext } from '@providers/userContextProvider';
import { Box, Typography } from 'hephaestus/web';
import { type ChangeEvent, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';

export type SelectHouseholdParentModelPortfolioOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  themeLabel?: TranslatedString;
  modelPortfolioLabel?: TranslatedString;
  tradingProcessLabel?: TranslatedString;
  /** When true, the advisor picks a trading process; otherwise NO_AUTOMATION is used. */
  showTradingProcess?: boolean;
};

export type SelectHouseholdParentModelPortfolioSelection = {
  financialProductId: string;
  tradingProcess: string;
};

/** Prior selection to restore, e.g. when the user navigates back to this step. */
export type SelectHouseholdParentModelPortfolioInitialSelection = {
  themeId?: string;
  financialProductId?: string;
  tradingProcess?: string;
};

export type SelectHouseholdParentModelPortfolioVisualProps = {
  options: SelectHouseholdParentModelPortfolioOptions;
  loading?: boolean;
  onNext: (
    selection: SelectHouseholdParentModelPortfolioSelection,
  ) => void | Promise<void>;
  initialSelection?: SelectHouseholdParentModelPortfolioInitialSelection;
};

export const SelectHouseholdParentModelPortfolioVisual = ({
  options,
  loading = false,
  onNext,
  initialSelection,
}: SelectHouseholdParentModelPortfolioVisualProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const { activeOrganization } = useContext(UserContext);
  const tradingProcessEnabled = options.showTradingProcess === true;

  // themeId is UI-only: it narrows ModelPortfoliosSelect. Only the resolved
  // financialProductId (base model) and tradingProcess are persisted downstream.
  const [themeId, setThemeId] = useState(initialSelection?.themeId ?? '');
  const [financialProductId, setFinancialProductId] = useState(
    initialSelection?.financialProductId ?? '',
  );
  const [tradingProcess, setTradingProcess] = useState(
    tradingProcessEnabled
      ? (initialSelection?.tradingProcess ??
          activeOrganization?.defaultTradingProcess ??
          TradingProcessValue.NoAutomation)
      : TradingProcessValue.NoAutomation,
  );

  const effectiveTradingProcess = tradingProcessEnabled
    ? tradingProcess
    : TradingProcessValue.NoAutomation;

  const canContinue =
    Boolean(
      financialProductId && (tradingProcessEnabled ? tradingProcess : true),
    ) && !loading;

  return (
    <WorkflowCompletionForm
      onSubmit={() =>
        onNext({ financialProductId, tradingProcess: effectiveTradingProcess })
      }
      submitButtonProps={{
        label: t('continue'),
        disabled: !canContinue,
        dataTestId: 'select-household-parent-model-portfolio-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackendString(options?.subtitle) || ''}
        </ReactMarkdown>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <ThemeSelect
          value={themeId}
          label={translateBackendString(options?.themeLabel)}
          onChange={(newValue: string) => {
            setFinancialProductId('');
            setThemeId(newValue);
          }}
        />
        {themeId && (
          <ModelPortfoliosSelect
            value={financialProductId}
            themeId={themeId}
            label={translateBackendString(options?.modelPortfolioLabel)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFinancialProductId(e.target.value)
            }
            suggestableOnly
          />
        )}
        {tradingProcessEnabled && (
          <SelectField
            label={translateBackendString(options?.tradingProcessLabel)}
            value={tradingProcess}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setTradingProcess(e.target.value)
            }
            fullWidth
            required
            testId='trading-process-select'
          >
            {TRADING_PROCESS_LIST.map((x) => (
              <MenuItem key={x.value} value={x.value}>
                {x.name}
              </MenuItem>
            ))}
          </SelectField>
        )}
      </Box>
    </WorkflowCompletionForm>
  );
};
